/**
 * useDocumentMeta — lightweight document.title + meta hook.
 *
 * Sets:
 *   - <title>
 *   - <meta name="description">
 *   - <meta name="keywords">
 *   - <link rel="canonical">
 *   - <meta property="og:*"> and <meta name="twitter:*"> if provided
 *
 * Restores prior values on unmount so navigating away doesn't leak.
 *
 * Kept in-tree instead of pulling in react-helmet-async since we only need
 * ~6 tags per page and this footprint is <2KB.
 */
import { useEffect } from 'react';

const upsertMeta = (attr, key, content) => {
  if (!content) return null;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  const prev = el ? el.getAttribute('content') : null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return { el, prev, wasCreated: prev === null };
};

const upsertLink = (rel, href) => {
  if (!href) return null;
  let el = document.querySelector(`link[rel="${rel}"]`);
  const prev = el ? el.getAttribute('href') : null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return { el, prev, wasCreated: prev === null };
};

const useDocumentMeta = ({ title, description, keywords, ogImage, ogType = 'website', canonical } = {}) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const created = [];
    const record = (r) => { if (r) created.push(r); };

    record(upsertMeta('name', 'description', description));
    record(upsertMeta('name', 'keywords', keywords));
    record(upsertMeta('property', 'og:title', title));
    record(upsertMeta('property', 'og:description', description));
    record(upsertMeta('property', 'og:type', ogType));
    record(upsertMeta('property', 'og:image', ogImage));
    record(upsertMeta('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary'));
    record(upsertMeta('name', 'twitter:title', title));
    record(upsertMeta('name', 'twitter:description', description));

    const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : null);
    record(upsertLink('canonical', canonicalUrl));

    return () => {
      document.title = prevTitle;
      // Restore prior values (or remove elements we created).
      for (const r of created) {
        if (!r) continue;
        if (r.wasCreated) {
          try { r.el.remove(); } catch (_) { /* ignore */ }
        } else if (r.prev != null) {
          try {
            const attr = r.el.tagName === 'LINK' ? 'href' : 'content';
            r.el.setAttribute(attr, r.prev);
          } catch (_) { /* ignore */ }
        }
      }
    };
  }, [title, description, keywords, ogImage, ogType, canonical]);
};

export default useDocumentMeta;
